/**
 * apps/wildswim-web/src/App.tsx
 *
 * Plan §20.6 — root router. All pages are lazy-loaded so the
 * landing page bundle stays small (target <120 KB gzipped for
 * first-paint).
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Shell } from './components/Shell';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const TodayPage = lazy(() => import('./pages/TodayPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const PlannerPage = lazy(() => import('./pages/PlannerPage'));
const FinderPage = lazy(() => import('./pages/FinderPage'));
const SpotDetailPage = lazy(() => import('./pages/SpotDetailPage'));
const SavedPage = lazy(() => import('./pages/SavedPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const SwimDetailPage = lazy(() => import('./pages/SwimDetailPage'));
const SwimModePage = lazy(() => import('./pages/SwimModePage'));
const PollutionHistoryPage = lazy(() => import('./pages/PollutionHistoryPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DataPage = lazy(() => import('./pages/DataPage'));
const WaterCompaniesPage = lazy(() => import('./pages/WaterCompaniesPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const YearInColdPage = lazy(() => import('./pages/YearInColdPage'));
const DigestPage = lazy(() => import('./pages/DigestPage'));
const RegionsIndexPage = lazy(() => import('./pages/RegionsIndexPage'));
const RegionPage = lazy(() => import('./pages/RegionPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ClubsPage = lazy(() => import('./pages/ClubsPage'));
const ClubNewPage = lazy(() => import('./pages/ClubNewPage'));
const ClubJoinPage = lazy(() => import('./pages/ClubJoinPage'));
const ClubConsolePage = lazy(() => import('./pages/ClubConsolePage'));
const ClubPublicPage = lazy(() => import('./pages/ClubPublicPage'));

function PageLoader() {
    return (
        <div className="flex items-center justify-center min-h-[40vh] text-hattie-mist">
            <span className="animate-pulse">Loading…</span>
        </div>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <Shell>
                <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/today" element={<TodayPage />} />
                        <Route path="/map" element={<MapPage />} />
                        <Route path="/planner" element={<PlannerPage />} />
                        <Route path="/finder" element={<FinderPage />} />
                        <Route path="/spots/:id" element={<SpotDetailPage />} />
                        <Route path="/saved" element={<SavedPage />} />
                        <Route path="/journal" element={<JournalPage />} />
                        <Route path="/journal/:id" element={<SwimDetailPage />} />
                        <Route path="/swim-mode" element={<SwimModePage />} />
                        <Route path="/pollution-history" element={<PollutionHistoryPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/onboarding" element={<OnboardingPage />} />
                        <Route path="/data" element={<DataPage />} />
                        <Route path="/water-companies" element={<WaterCompaniesPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/press" element={<PressPage />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/admin/*" element={<AdminPage />} />
                        {/* Plan §37 S7 — clubs. Join is PUBLIC (the
                          WhatsApp landing); the console nests its own
                          routes AdminPage-style. */}
                        <Route path="/clubs" element={<ClubsPage />} />
                        <Route path="/clubs/new" element={<ClubNewPage />} />
                        <Route path="/clubs/join/:token" element={<ClubJoinPage />} />
                        <Route path="/clubs/:clubId/*" element={<ClubConsolePage />} />
                        {/* §38 S8 — the club's PUBLIC page; signed-out, allowlist payload only. */}
                        <Route path="/c/:slug" element={<ClubPublicPage />} />
                        <Route path="/year-in-cold" element={<YearInColdPage />} />
                        <Route path="/digest" element={<DigestPage />} />
                        {/* Regional SEO hubs (June 2026) — "wild swimming
                          in {region}" landing surfaces over the spot pages */}
                        <Route path="/swimming" element={<RegionsIndexPage />} />
                        <Route path="/swimming/:slug" element={<RegionPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </Suspense>
            </Shell>
        </BrowserRouter>
    );
}
